import {createContext, useCallback, useContext, useState, useEffect, useMemo} from "react";
import { AuthContext } from "./authContext";
import { API_BASE_URL, API_KEY } from "../config/config";
import axios from "axios";
import { useParams } from "react-router-dom";

const ThreadVideoContext = createContext(null);

const ThreadVideoProvider = ({ children }) => {
	const { visitor } = useContext(AuthContext);
	const { threadTitleId } = useParams();

	// State management
	const [threadState, setThreadState] = useState({
		thread: null,
		loading: false,
		error: null
	});

	const [videosState, setVideosState] = useState({
		videos: [],
		loading: false,
		error: null,
		initialLoaded: false,
		page: 1,  // Initialize page parameter
		hasMore: false
	});

	// Extract thread ID from URL parameter
	const threadId = useMemo(() => {
		if (!threadTitleId) return null;

		const urlPattern = /^([\w-]+)\.(\d+)$/;
		const match = threadTitleId.match(urlPattern);

		if (match && match[2]) {
			return Number(match[2]);
		}

		return null;
	}, [threadTitleId]);

	// Create API request config with authentication
	const createApiConfig = useCallback((endpoint) => {
		if (!visitor) return null;

		return {
			method: 'get',
			url: `${API_BASE_URL}${endpoint}`,
			headers: {
				'XF-Api-Key': API_KEY,
				'XF-Api-User': visitor.user_id,
			},
			withCredentials: true,
		};
	}, [visitor]);

	// Fetch thread data
	const fetchThread = useCallback(async (isInitialLoad = false) => {
		if (!visitor || !threadId) return;
		if (threadState.thread !== null && isInitialLoad) return; // Already loaded
		if (threadState.loading) return; // Already loading

		try {
			setThreadState(prev => ({ ...prev, loading: true, error: null }));

			const config = createApiConfig(`/threads/${threadId}`);
			if (!config) return;

			const response = await axios(config);
			const fetchedThread = response.data.thread;

			setThreadState({
				thread: fetchedThread,
				loading: false,
				error: null
			});
		} catch (error) {
			console.error('Failed to fetch thread:', error);
			setThreadState(prev => ({
				...prev,
				loading: false,
				error: error.message || 'Failed to fetch thread data'
			}));
		}
	}, [visitor, threadId, threadState.thread, threadState.loading, createApiConfig]);

	// Fetch videos for current thread
	const fetchVideos = useCallback(async (reset = false) => {
		if (!visitor || !threadId) return;
		if (videosState.loading) return; // Already loading
		if (!videosState.hasMore && !reset) return; // No more videos to load

		try {
			setVideosState(prev => ({
				...prev,
				loading: true,
				error: null,
				...(reset ? { videos: [], page: 1, hasMore: true } : {})
			}));

			const page = reset ? 1 : videosState.page;
			const config = createApiConfig(`/threads/${threadId}/video-entities?page=${page}`);
			if (!config) return;

			const response = await axios(config);
			const newVideos = Array.isArray(response.data.videos)
				? response.data.videos
				: Object.values(response.data.videos || {});

			// Check if we've reached the end of available videos
			const hasMore = newVideos.length > 0;

			setVideosState(prev => ({
				videos: reset ? newVideos : [...prev.videos, ...newVideos],
				loading: false,
				error: null,
				initialLoaded: true,
				page: reset ? 2 : prev.page + 1,
				hasMore
			}));
		} catch (error) {
			console.error("Failed to fetch videos:", error);
			setVideosState(prev => ({
				...prev,
				loading: false,
				error: error.message || 'Failed to fetch videos',
				initialLoaded: true,
			}));
		}
	}, [visitor, threadId, videosState.loading, videosState.page, videosState.hasMore, createApiConfig]);

	// Load more videos
	const loadMoreVideos = useCallback(() => {
		if (!videosState.loading && videosState.hasMore) {
			fetchVideos();
		}
	}, [fetchVideos, videosState.loading, videosState.hasMore]);

	// Auto-fetch thread data when component mounts or threadId changes
	useEffect(() => {
		if (threadId) {
			fetchThread(true);
		}
	}, [threadId, fetchThread]);

	// Return context value
	const contextValue = {
		thread: threadState.thread,
		threadLoading: threadState.loading,
		threadError: threadState.error,
		videosData: {
			videos: videosState.videos,
			loading: videosState.loading,
			error: videosState.error,
			initialLoaded: videosState.initialLoaded,
			hasMore: videosState.hasMore
		},
		fetchVideos,
		loadMoreVideos,
		fetchThread
	};

	return (
		<ThreadVideoContext.Provider value={contextValue}>
			{children}
		</ThreadVideoContext.Provider>
	);
};

export { ThreadVideoContext, ThreadVideoProvider };